import { z } from 'zod';
import {
  IsoDateCodec,
  IsoDatetimeToDateSchema,
  NullableIsoDateCodec,
  NullableIsoDatetimeToDateSchema,
} from './date-codecs.js';
import { UuidSchema } from './primitives/uuid.js';

// Core arena analysis types
export const ArenaParticipantSchema = z.object({
  profileId: z.string(),
  username: z.string(),
  summary: z.object({
    workStyle: z.string().min(1),
    character: z.string().min(1),
    problemSolving: z.string().min(1),
    interests: z.array(z.string()).default([]),
  }),
});

export type ArenaParticipant = z.infer<typeof ArenaParticipantSchema>;

export const ArenaPairwiseSchema = z.object({
  a: z.string(),
  b: z.string(),
  score: z.number().min(0).max(100),
  reasoning: z.string().min(1),
});

export type ArenaPairwise = z.infer<typeof ArenaPairwiseSchema>;

export const ArenaTeamSchema = z.object({
  score: z.number().min(0).max(100),
  reasoning: z.string().min(1),
});

export type ArenaTeam = z.infer<typeof ArenaTeamSchema>;

export const ArenaAnalysisSchema = z
  .object({
    participants: z.array(ArenaParticipantSchema).min(2).max(4),
    pairwise: z.array(ArenaPairwiseSchema).min(1),
    team: ArenaTeamSchema,
  })
  .passthrough();

export type ArenaAnalysis = z.infer<typeof ArenaAnalysisSchema>;

// API request/response schemas
export const ArenaCacheParticipantSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type ArenaCacheParticipant = z.infer<typeof ArenaCacheParticipantSchema>;

export const ArenaAnalyzeRequestSchema = z.object({
  profileIds: z.array(z.string().min(1)).min(2).max(4),
  model: z.string().optional(),
  tweetsPerUser: z.number().int().positive().optional(),
  forceRefresh: z.boolean().optional().default(false),
});

export type ArenaAnalyzeRequest = z.infer<typeof ArenaAnalyzeRequestSchema>;

export const ArenaAnalyzeResponseSchema = z.object({
  success: z.boolean(),
  analysisId: UuidSchema,
  cacheKey: z.string(),
});

export type ArenaAnalyzeResponse = z.infer<typeof ArenaAnalyzeResponseSchema>;

export const ArenaCacheStatusSchema = z.enum(['pending', 'running', 'completed', 'failed', 'cancelled']);

export type ArenaCacheStatus = z.infer<typeof ArenaCacheStatusSchema>;

export const ArenaCacheListItemSchema = z.object({
  id: z.string(),
  participants: z.array(ArenaCacheParticipantSchema),
  cacheKey: z.string(),
  status: ArenaCacheStatusSchema,
  createdAt: IsoDateCodec,
  completedAt: NullableIsoDateCodec,
  errorMessage: z.string().nullable(),
  ownedByCurrentUser: z.boolean(),
});

export type ArenaCacheListItem = z.infer<typeof ArenaCacheListItemSchema>;

export const ArenaCacheListQuerySchema = z.object({
  limit: z.number().int().min(1).max(100).optional(),
  status: ArenaCacheStatusSchema.optional(),
});

export type ArenaCacheListQuery = z.infer<typeof ArenaCacheListQuerySchema>;

export const ArenaCacheListResponseSchema = z.object({
  items: z.array(ArenaCacheListItemSchema),
});

export type ArenaCacheListResponse = z.infer<typeof ArenaCacheListResponseSchema>;

export const ArenaCacheDetailResponseSchema = z.object({
  id: z.string(),
  participants: z.array(ArenaCacheParticipantSchema),
  status: ArenaCacheStatusSchema,
  createdAt: IsoDateCodec,
  completedAt: NullableIsoDateCodec,
  errorMessage: z.string().nullable(),
  metadata: z.unknown().optional(),
  result: ArenaAnalysisSchema.nullable(),
});

export type ArenaCacheDetailResponse = z.infer<typeof ArenaCacheDetailResponseSchema>;

const ArenaCacheTimestampsBoundarySchema = z
  .object({
    createdAt: IsoDatetimeToDateSchema,
    completedAt: NullableIsoDatetimeToDateSchema,
  })
  .passthrough();

export const ArenaCacheListItemBoundarySchema = ArenaCacheTimestampsBoundarySchema.pipe(ArenaCacheListItemSchema);

export const ArenaCacheListResponseBoundarySchema = z
  .object({
    items: z.array(ArenaCacheListItemBoundarySchema),
  })
  .pipe(ArenaCacheListResponseSchema);

export const ArenaCacheDetailResponseBoundarySchema =
  ArenaCacheTimestampsBoundarySchema.pipe(ArenaCacheDetailResponseSchema);

export type ArenaCacheListResponseInput = z.input<typeof ArenaCacheListResponseSchema>;
export type ArenaCacheDetailResponseInput = z.input<typeof ArenaCacheDetailResponseSchema>;

export function parseArenaCacheListResponse(input: ArenaCacheListResponseInput): ArenaCacheListResponse {
  return ArenaCacheListResponseBoundarySchema.parse(input);
}

export function parseArenaCacheDetailResponse(input: ArenaCacheDetailResponseInput): ArenaCacheDetailResponse {
  return ArenaCacheDetailResponseBoundarySchema.parse(input);
}

export const ArenaCacheDeleteResponseSchema = z.object({
  success: z.boolean(),
  id: z.string(),
});

export type ArenaCacheDeleteResponse = z.infer<typeof ArenaCacheDeleteResponseSchema>;

export const ArenaRealtimeTokenRequestSchema = z.object({
  analysisId: UuidSchema,
});

export { RealtimeTokenSchema as ArenaRealtimeTokenResponseSchema } from './shared/realtime.js';
